import React, { useState, useEffect, useRef } from 'react';
import './artisanmessages.scss';
import withAuth from '../../auth/WithAuth';
import FurnitureNavbar from '../../../components/layout/Nav';
import { serverUrlAuth } from '../../../serverUrl';
import { toast } from 'react-toastify';
import {
  FaSearch,
  FaUserCircle,
  FaComments,
  FaPaperPlane,
  FaArrowLeft,
  FaCheckDouble,
  FaClock,
  FaPhone,
  FaVideo,
  FaEllipsisV,
  FaImage,
  FaCheck,
  FaHandshake,
  FaMoneyBillWave,
  FaTimes,
  FaDownload,
  FaFlag,
  FaEdit,
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

const ArtisanMessages = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const preselectedProposalId = queryParams.get('proposal');

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const [negotiationDetails, setNegotiationDetails] = useState(null);
  const [projectStatus, setProjectStatus] = useState(null);
  const [isArtisanSelected, setIsArtisanSelected] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [isLoadingProjectStatus, setIsLoadingProjectStatus] = useState(false);
  const [proposalDetails, setProposalDetails] = useState(null);

  // Message type for project updates
  const [messageType, setMessageType] = useState('chat');
  const [progressPercentage, setProgressPercentage] = useState('');
  const [milestoneNote, setMilestoneNote] = useState('');

  // Image upload states
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);

  // Modal states
  const [showFinalAgreementModal, setShowFinalAgreementModal] = useState(false);
  const [finalAgreementAmount, setFinalAgreementAmount] = useState('');
  const [isSubmittingFinalAgreement, setIsSubmittingFinalAgreement] = useState(false);

  // Edit Final Agreement Modal states
  const [showEditFinalAgreementModal, setShowEditFinalAgreementModal] = useState(false);
  const [editFinalAgreementAmount, setEditFinalAgreementAmount] = useState('');
  const [isEditingFinalAgreement, setIsEditingFinalAgreement] = useState(false);
  const [editAgreementNote, setEditAgreementNote] = useState('');

  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImageForView, setSelectedImageForView] = useState(null);

  const messagesEndRef = useRef(null);
  const messageIntervalRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isPollingRef = useRef(false);

  // Mark all messages as read when opening a conversation
  const markMessagesAsRead = async (projectId) => {
    if (!projectId) return;
    
    try {
      console.log('Marking messages as read for project:', projectId);
      const response = await serverUrlAuth.post(`/updates/mark-messages-as-read/${projectId}/mark-all-read/`);
      console.log('Mark as read response:', response.data);
      
      // Update conversation list to reflect read status
      const convResponse = await serverUrlAuth.get('negotiations/inbox/');
      setConversations(convResponse.data);
      
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  // Fetch proposal details
  const fetchProposalDetails = async (proposalId) => {
    try {
      const response = await serverUrlAuth.get(`proposals/proposal-detail/${proposalId}/`);
      console.log('Proposal details:', response.data);
      setProposalDetails(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching proposal details:', err);
      return null;
    }
  };

  // Edit Final Agreement - reuse the same submit-final-agreement endpoint
  const handleEditFinalAgreement = async () => {
    if (!editFinalAgreementAmount || editFinalAgreementAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsEditingFinalAgreement(true);
    try {
      // Reuse the same submit-final-agreement endpoint to update the pending amount
      await serverUrlAuth.patch(`negotiations/submit-final-agreement/${negotiationDetails.id}/`, {
        final_agreed_amount: editFinalAgreementAmount,
      });

      toast.success('Final agreement amount updated successfully! Waiting for client acceptance.');
      setShowEditFinalAgreementModal(false);
      setEditFinalAgreementAmount('');
      setEditAgreementNote('');
      
      // Refresh negotiation details
      await fetchNegotiationDetails(selectedConversation.proposal);
      
      // Send a follow-up message with the note (if provided)
      if (editAgreementNote.trim()) {
        await serverUrlAuth.post(
          `negotiations/proposal/${selectedConversation?.proposal}/messages/`,
          { 
            message: `📝 Note about the updated agreement: ${editAgreementNote}`
          }
        );
      }
      
      // Refresh messages
      await fetchMessages(currentProjectId);
      
    } catch (err) {
      console.error('Error updating final agreement:', err);
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Failed to update final agreement');
    } finally {
      setIsEditingFinalAgreement(false);
    }
  };

  // Fetch project status
  const fetchProjectStatus = async (projectId) => {
    if (!projectId) return false;

    setIsLoadingProjectStatus(true);
    try {
      console.log('Fetching project status for projectId:', projectId);

      try {
        const response = await serverUrlAuth.get(`projects/artisan-project/${projectId}/`);
        console.log('Artisan project response:', response.data);

        if (response.data) {
          const isSelected = response.data.status === 'in-progress' || response.data.status === 'completed';
          setProjectStatus(response.data.status);
          setIsArtisanSelected(isSelected);
          console.log('isArtisanSelected set to:', isSelected, 'from artisan-project endpoint');
          return isSelected;
        }
      } catch (err) {
        console.log('Artisan project endpoint failed, checking matched projects...');
      }

      const response = await serverUrlAuth.get('projects/artisan/matched-projects/');
      const matchedProject = response.data.find((p) => p.project === projectId || p.id === projectId);

      if (matchedProject) {
        const proposalsRes = await serverUrlAuth.get(`proposals/proposals-list/${projectId}/`);
        const isSelected = proposalsRes.data.some((p) => p.is_selected === true);

        setProjectStatus(isSelected ? 'in-progress' : 'pending');
        setIsArtisanSelected(isSelected);
        console.log('isArtisanSelected set to:', isSelected, 'from matched projects');
        return isSelected;
      }

      return false;
    } catch (err) {
      console.error('Error fetching project status:', err);
      return false;
    } finally {
      setIsLoadingProjectStatus(false);
    }
  };

  // Fetch all conversations
  const fetchConversations = async () => {
    try {
      const response = await serverUrlAuth.get('negotiations/inbox/');
      console.log('Conversations:', response.data);
      setConversations(response.data);

      if (preselectedProposalId && !selectedConversation) {
        let conversation = response.data.find((c) => c.proposal?.toString() === preselectedProposalId);

        if (conversation) {
          const projectId = conversation.proposal_details?.project;
          setCurrentProjectId(projectId);
          setSelectedConversation(conversation);
          setShowSidebar(false);

          await fetchProposalDetails(conversation.proposal);
          await fetchProjectStatus(projectId);
          await fetchNegotiationDetails(conversation.proposal);
          await fetchMessages(projectId);
          await markMessagesAsRead(projectId);
        } else {
          await fetchSingleConversation(preselectedProposalId);
        }
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  // Fetch single conversation
  const fetchSingleConversation = async (proposalId) => {
    try {
      const response = await serverUrlAuth.get(`negotiations/proposal/${proposalId}/`);
      const convData = response.data;
      console.log('Single conversation:', convData);

      const projectId = convData.proposal_details?.project;

      const newConversation = {
        id: convData.id,
        proposal: proposalId,
        project_id: projectId,
        project_title: convData.project_title,
        artisan_name: convData.artisan_name,
        client_name: convData.client_name,
        last_message_time: convData.created_at,
        unread_count: 0,
        budget: convData.proposal_details?.proposed_price,
      };

      setCurrentProjectId(projectId);
      setSelectedConversation(newConversation);
      setShowSidebar(false);
      setNegotiationDetails(convData);

      await fetchProposalDetails(proposalId);
      await fetchProjectStatus(projectId);
      await fetchMessages(projectId);
      await markMessagesAsRead(projectId);
    } catch (err) {
      console.error('Error fetching single conversation:', err);
      toast.error('Could not open conversation');
    }
  };

  // Fetch messages from COMBINED endpoint
  const fetchMessages = async (projectId, isPolling = false) => {
    if (!projectId) {
      console.log('No projectId, skipping fetch');
      return;
    }

    if (!isPolling) {
      setLoadingMessages(true);
    }
    try {
      console.log('Fetching messages for project:', projectId);
      const response = await serverUrlAuth.get(`/updates/messages/${projectId}/`);
      console.log('Messages received:', response.data);
      setMessages(response.data);
      scrollToBottom();
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      if (!isPolling) {
        setLoadingMessages(false);
      }
    }
  };

  // Fetch negotiation details
  const fetchNegotiationDetails = async (proposalId) => {
    try {
      const response = await serverUrlAuth.get(`negotiations/proposal/${proposalId}/`);
      console.log('Negotiation details:', response.data);
      setNegotiationDetails(response.data);
    } catch (err) {
      console.error('Error fetching negotiation details:', err);
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const validFiles = files.filter((file) => file.type.startsWith('image/'));
      if (validFiles.length !== files.length) {
        toast.warning('Only image files are allowed');
      }
      const newImages = [...selectedImages, ...validFiles];
      const newPreviews = [...imagePreviewUrls, ...validFiles.map((file) => URL.createObjectURL(file))];
      setSelectedImages(newImages);
      setImagePreviewUrls(newPreviews);
    }
  };

  // Remove selected image
  const removeSelectedImage = (index) => {
    URL.revokeObjectURL(imagePreviewUrls[index]);
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviewUrls.filter((_, i) => i !== index);
    setSelectedImages(newImages);
    setImagePreviewUrls(newPreviews);
  };

  // Clear all images
  const clearImages = () => {
    imagePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    setSelectedImages([]);
    setImagePreviewUrls([]);
  };

  // Send message based on project phase
  const sendMessage = async () => {
    if (isArtisanSelected) {
      if (messageType === 'milestone') {
        if (!milestoneNote.trim()) {
          toast.error('Please add a note for the milestone');
          return;
        }
        if (!progressPercentage || progressPercentage < 0 || progressPercentage > 100) {
          toast.error('Please enter a valid progress percentage (0-100)');
          return;
        }
      } else {
        if (!newMessage.trim() && selectedImages.length === 0) {
          toast.error('Please enter a message or attach an image');
          return;
        }
      }
    } else {
      if (!newMessage.trim() && selectedImages.length === 0) return;
    }

    setIsSending(true);
    setUploadingImages(true);

    try {
      let response;

      console.log('Sending message - isArtisanSelected:', isArtisanSelected, 'currentProjectId:', currentProjectId);

      if (isArtisanSelected && currentProjectId) {
        console.log('✅ Sending to UPDATES app (project in progress)');
        const formData = new FormData();
        formData.append('type', messageType);

        if (messageType === 'milestone') {
          formData.append('progress_percentage', progressPercentage);
          formData.append('note', milestoneNote);
        } else {
          formData.append('message', newMessage.trim() || '📷 Image attached');
        }

        selectedImages.forEach((image) => {
          formData.append('uploaded_images', image);
        });

        response = await serverUrlAuth.post(`/updates/post-updates/${currentProjectId}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        setProgressPercentage('');
        setMilestoneNote('');
      } else {
        console.log('⚠️ Sending to NEGOTIATIONS app (negotiation phase)');
        const formData = new FormData();
        formData.append('message', newMessage.trim() || '📷 Image attached');
        selectedImages.forEach((image) => {
          formData.append('images', image);
        });

        response = await serverUrlAuth.post(
          `negotiations/proposal/${selectedConversation.proposal}/messages/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }

      if (response.data) {
        setMessages((prev) => [...prev, response.data]);
        setNewMessage('');
        clearImages();
        scrollToBottom();

        const convResponse = await serverUrlAuth.get('negotiations/inbox/');
        setConversations(convResponse.data);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      if (err.response?.data?.detail?.includes('restricted content')) {
        toast.error('⚠️ Message blocked: Contains restricted content (phone numbers, emails)');
      } else if (err.response?.data?.progress_percentage) {
        toast.error(err.response.data.progress_percentage[0]);
      } else {
        toast.error('Failed to send message.');
      }
    } finally {
      setIsSending(false);
      setUploadingImages(false);
    }
  };

  // Submit final agreement
  const handleSubmitFinalAgreement = async () => {
    if (!finalAgreementAmount || !selectedConversation || !negotiationDetails) return;

    setIsSubmittingFinalAgreement(true);
    try {
      await serverUrlAuth.patch(`negotiations/submit-final-agreement/${negotiationDetails.id}/`, {
        final_agreed_amount: finalAgreementAmount,
      });

      toast.success(`Final agreement submitted! Waiting for client acceptance.`);
      setShowFinalAgreementModal(false);
      setFinalAgreementAmount('');
      await fetchNegotiationDetails(selectedConversation.proposal);
      await fetchMessages(currentProjectId);
    } catch (err) {
      console.error('Error submitting final agreement:', err);
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Failed to submit final agreement');
    } finally {
      setIsSubmittingFinalAgreement(false);
    }
  };

  // Select conversation
  const handleSelectConversation = async (conversation) => {
    const projectId = conversation.proposal_details?.project;
    setCurrentProjectId(projectId);
    setSelectedConversation(conversation);

    await fetchProposalDetails(conversation.proposal);
    await fetchProjectStatus(projectId);
    await fetchNegotiationDetails(conversation.proposal);
    await fetchMessages(projectId);
    await markMessagesAsRead(projectId);
    setShowSidebar(false);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const startPolling = () => {
    if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
    
    messageIntervalRef.current = setInterval(async () => {
      if (selectedConversation && currentProjectId && !isPollingRef.current) {
        isPollingRef.current = true;
        try {
          const currentMessageCount = messages.length;
          await fetchMessages(currentProjectId, true);
          
          // Refresh conversation list to update unread counts
          const convResponse = await serverUrlAuth.get('negotiations/inbox/');
          setConversations(convResponse.data);
          
          if (messages.length > currentMessageCount) {
            scrollToBottom();
          }
        } catch (error) {
          console.error('Polling error:', error);
        } finally {
          isPollingRef.current = false;
        }
      }
    }, 5000);
  };

  const stopPolling = () => {
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
  };

  const filteredConversations = conversations.filter(
    (conv) =>
      conv.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.project_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) {
      const mins = Math.floor(diff / (1000 * 60));
      return mins === 0 ? 'Just now' : `${mins} min ago`;
    } else if (hours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (hours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getUnreadCount = (conversation) => conversation.unread_count || 0;
  const getDisplayName = (conversation) => conversation.client_name;

  useEffect(() => {
    fetchConversations();
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (selectedConversation && currentProjectId) {
      startPolling();
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [selectedConversation, currentProjectId]);

  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreviewUrls]);

  const hasPendingAgreement = negotiationDetails?.pending_final_amount && !negotiationDetails?.final_agreed_amount;
  const isChatDisabled = negotiationDetails?.is_closed === true;
  const isNegotiationPhase = !isArtisanSelected && !isChatDisabled;

  console.log('Current state:', {
    currentProjectId,
    isArtisanSelected,
    messagesCount: messages.length,
    hasPendingAgreement,
    pendingAmount: negotiationDetails?.pending_final_amount,
  });

  return (
    <>
      <FurnitureNavbar />
      <div className="messages-page">
        <div className="whatsapp-container">
          {/* Sidebar */}
          <div className={`conversations-sidebar ${showSidebar ? 'open' : 'closed'}`}>
            <div className="sidebar-header">
              <div className="header-top">
                <h2>Chats</h2>
              </div>
              <div className="search-bar">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="conversations-list">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading chats...</p>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">💬</div>
                  <h4>No chats yet</h4>
                  <p>When clients message you, conversations will appear here.</p>
                </div>
              ) : (
                filteredConversations.map((conv) => {
                  const unreadCount = getUnreadCount(conv);
                  const isSelected = selectedConversation?.id === conv.id;

                  return (
                    <div
                      key={conv.id}
                      className={`conversation-item ${isSelected ? 'active' : ''} ${unreadCount > 0 ? 'unread' : ''}`}
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <div className="avatar">
                        <FaUserCircle />
                      </div>
                      <div className="conversation-info">
                        <div className="conversation-header">
                          <h4>{getDisplayName(conv)}</h4>
                          <span className="timestamp">{formatTime(conv.last_message_time)}</span>
                        </div>
                        <div className="conversation-preview">
                          <p className="project-title">{conv.project_title}</p>
                        </div>
                      </div>
                      {unreadCount > 0 && <div className="unread-badge">{unreadCount}</div>}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="chat-area">
            {!selectedConversation ? (
              <div className="no-conversation-selected">
                <div className="no-chat-icon">
                  <FaComments />
                </div>
                <h3>Select a conversation</h3>
                <p>Choose a chat from the list to start messaging</p>
                <button className="back-to-chats-btn" onClick={() => setShowSidebar(true)}>
                  Back to Chats
                </button>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="chat-header">
                  <button
                    className="back-button"
                    onClick={() => {
                      setShowSidebar(true);
                      setSelectedConversation(null);
                    }}
                  >
                    <FaArrowLeft />
                  </button>
                  <div className="chat-user-info">
                    <div className="user-avatar">
                      <FaUserCircle />
                    </div>
                    <div className="user-details">
                      <h3>{getDisplayName(selectedConversation)}</h3>
                      <p className="project-context">Project: {selectedConversation.project_title}</p>
                    </div>
                  </div>
                </div>

                {/* Status Banner */}
                {isArtisanSelected ? (
                  <div className="project-status-banner">
                    <div className="status-info">
                      <FaCheck className="status-icon" />
                      <span>Project In Progress</span>
                    </div>
                    <div className="info-note">💬 Send updates and milestones to keep the client informed</div>
                  </div>
                ) : (
                  negotiationDetails && (
                    <div className="price-status-banner">
                      {negotiationDetails.final_agreed_amount ? (
                        <div className="price-accepted">
                          <FaHandshake /> Price Agreed: KSh {Number(negotiationDetails.final_agreed_amount).toLocaleString()}
                        </div>
                      ) : negotiationDetails.pending_final_amount ? (
                        <div className="price-pending">
                          <div className="pending-header">
                            <FaMoneyBillWave /> Pending Agreement: KSh {Number(negotiationDetails.pending_final_amount).toLocaleString()}
                            <span className="waiting-text">Waiting for client acceptance...</span>
                          </div>
                          {/* Edit button for pending agreement */}
                          <button
                            className="edit-pending-agreement-btn"
                            onClick={() => {
                              setEditFinalAgreementAmount(negotiationDetails.pending_final_amount);
                              setShowEditFinalAgreementModal(true);
                            }}
                            title="Edit pending agreement amount"
                          >
                            <FaEdit /> Edit Amount
                          </button>
                        </div>
                      ) : (
                        <div className="price-negotiating">
                          💬 Negotiating - Discuss price with the client
                        </div>
                      )}
                    </div>
                  )
                )}

                {/* Messages Area */}
                <div className="messages-area" ref={messagesContainerRef}>
                  {loadingMessages && messages.length === 0 ? (
                    <div className="loading-messages">
                      <div className="spinner"></div>
                      <p>Loading messages...</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="no-messages">
                      <div className="no-messages-icon">💬</div>
                      <p>No messages yet</p>
                      <small>Send a message to start the conversation</small>
                    </div>
                  ) : (
                    messages.map((msg, index) => {
                      const isSent = msg.sender_role === 'artisan';
                      const isMilestone = msg.type === 'milestone';
                      const showDate =
                        index === 0 ||
                        (messages[index - 1] &&
                          new Date(msg.timestamp || msg.created_at).toDateString() !==
                            new Date(messages[index - 1].timestamp || messages[index - 1].created_at).toDateString());

                      return (
                        <React.Fragment key={msg.id}>
                          {showDate && (
                            <div className="date-divider">
                              <span>
                                {new Date(msg.timestamp || msg.created_at).toLocaleDateString(undefined, {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </span>
                            </div>
                          )}
                          <div className={`message-bubble ${isSent ? 'sent' : 'received'}`}>
                            <div className="message-content">
                              {isMilestone ? (
                                <div className="milestone-update">
                                  <div className="milestone-header">
                                    <FaFlag className="milestone-icon" />
                                    <span className="milestone-label">Milestone Update</span>
                                  </div>
                                  <div className="progress-section">
                                    <div className="progress-info">
                                      <span>Progress: {msg.progress_percentage}%</span>
                                    </div>
                                    <div className="progress-bar-container">
                                      <div className="progress-fill" style={{ width: `${msg.progress_percentage}%` }} />
                                    </div>
                                  </div>
                                  <div className="milestone-note">{msg.note}</div>
                                </div>
                              ) : (
                                <p>{msg.message}</p>
                              )}
                              {msg.offer && (
                                <div className="offer-bubble">
                                  💰 Price Proposal: KSh {Number(msg.offer).toLocaleString()}
                                </div>
                              )}
                              {msg.images && msg.images.length > 0 && (
                                <div className="message-images">
                                  {msg.images.map((img, imgIndex) => (
                                    <img
                                      key={imgIndex}
                                      src={img.image_url || img.image}
                                      alt="Attachment"
                                      className="message-image"
                                      onClick={() => {
                                        setSelectedImageForView(img.image_url || img.image);
                                        setShowImageViewer(true);
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="message-meta">
                              <span className="message-time">{formatMessageTime(msg.created_at || msg.timestamp)}</span>
                              {isSent && (
                                <span className="read-status">
                                  {msg.read_by_client && msg.read_by_artisan ? <FaCheckDouble /> : <FaCheck />}
                                </span>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Image Preview Area */}
                {imagePreviewUrls.length > 0 && (
                  <div className="image-preview-area">
                    <div className="preview-header">
                      <span>{imagePreviewUrls.length} image(s) selected</span>
                      <button onClick={clearImages}>
                        <FaTimes /> Clear
                      </button>
                    </div>
                    <div className="preview-images">
                      {imagePreviewUrls.map((url, idx) => (
                        <div key={idx} className="preview-image-wrapper">
                          <img src={url} alt={`Preview ${idx + 1}`} />
                          <button className="remove-image" onClick={() => removeSelectedImage(idx)}>
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Chat Input Area */}
                <div className="message-input-area">
                  {/* Submit Final Agreement button - only during negotiation and no pending agreement */}
                  {isNegotiationPhase && !negotiationDetails?.final_agreed_amount && !hasPendingAgreement && (
                    <div className="price-action-buttons">
                      <button className="submit-agreement-btn" onClick={() => setShowFinalAgreementModal(true)}>
                        <FaHandshake /> Submit Final Agreement
                      </button>
                    </div>
                  )}

                  {/* Message Type Selector - only when project is in progress */}
                  {isArtisanSelected && !isChatDisabled && (
                    <div className="message-type-selector">
                      <button className={`type-btn ${messageType === 'chat' ? 'active' : ''}`} onClick={() => setMessageType('chat')}>
                        💬 Chat
                      </button>
                      <button className={`type-btn ${messageType === 'milestone' ? 'active' : ''}`} onClick={() => setMessageType('milestone')}>
                        🎯 Milestone
                      </button>
                    </div>
                  )}

                  {/* Milestone Inputs - only when milestone mode is selected */}
                  {isArtisanSelected && messageType === 'milestone' && (
                    <div className="milestone-inputs">
                      <div className="progress-input-wrapper">
                        <input
                          type="number"
                          placeholder="%"
                          value={progressPercentage}
                          onChange={(e) => setProgressPercentage(e.target.value)}
                          className="progress-input"
                          min="0"
                          max="100"
                        />
                      </div>
                      <textarea
                        placeholder="Describe the milestone achieved..."
                        value={milestoneNote}
                        onChange={(e) => setMilestoneNote(e.target.value)}
                        className="milestone-textarea"
                        rows={1}
                      />
                      <div className="milestone-actions">
                        <button className="attach-btn-milestone" onClick={() => fileInputRef.current?.click()} title="Attach image" type="button">
                          <FaImage />
                        </button>
                        <button
                          onClick={sendMessage}
                          disabled={!milestoneNote.trim() || !progressPercentage || isSending}
                          className="milestone-send-btn"
                        >
                          {isSending ? <FaClock /> : <FaPaperPlane />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Regular Chat Input - only when chat mode is selected */}
                  {(!isArtisanSelected || messageType === 'chat') && (
                    <div className="input-wrapper">
                      <button
                        className="attach-btn"
                        onClick={() => fileInputRef.current?.click()}
                        title="Attach image"
                        type="button"
                        disabled={isChatDisabled}
                      >
                        <FaImage />
                      </button>
                      <input
                        type="text"
                        placeholder={isChatDisabled ? 'Chat is closed - Project completed' : 'Type a message...'}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !isChatDisabled) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        className="message-input"
                        disabled={isChatDisabled}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={
                          isSending ||
                          isChatDisabled ||
                          (!isArtisanSelected && !newMessage.trim() && selectedImages.length === 0) ||
                          (isArtisanSelected && messageType === 'chat' && !newMessage.trim() && selectedImages.length === 0)
                        }
                        className="send-button"
                        type="button"
                      >
                        {isSending ? <FaClock /> : <FaPaperPlane />}
                      </button>
                    </div>
                  )}

                  {/* Hidden file input */}
                  <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" multiple onChange={handleImageSelect} />

                  {isChatDisabled && (
                    <div className="chat-disabled-note">
                      <small>This project is completed. Chat is closed.</small>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Artisan: Submit Final Agreement Modal */}
      <Modal show={showFinalAgreementModal} onHide={() => setShowFinalAgreementModal(false)} centered className="price-modal">
        <Modal.Header closeButton>
          <Modal.Title>Submit Final Agreement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="price-modal-content">
            <p className="info-text">Submit the final agreed price for this project. The client will need to accept it.</p>
            <div className="price-input-group">
              <label>Final Agreed Amount (KSh)</label>
              <input
                type="number"
                className="price-input"
                placeholder="Enter final agreed amount"
                value={finalAgreementAmount}
                onChange={(e) => setFinalAgreementAmount(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFinalAgreementModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmitFinalAgreement} disabled={!finalAgreementAmount || isSubmittingFinalAgreement}>
            {isSubmittingFinalAgreement ? 'Submitting...' : 'Submit Agreement'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Final Agreement Modal - Reusing the same endpoint */}
      <Modal show={showEditFinalAgreementModal} onHide={() => setShowEditFinalAgreementModal(false)} centered className="edit-agreement-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEdit /> Edit Final Agreement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="edit-agreement-content">
            <p className="info-text warning">
              ⚠️ You submitted a final agreement amount of KSh {Number(negotiationDetails?.pending_final_amount || 0).toLocaleString()}.
              The client hasn't accepted it yet. You can update it below.
            </p>
            <div className="form-group">
              <label>Corrected Final Agreed Amount (KSh) *</label>
              <input
                type="number"
                className="form-input"
                placeholder="Enter corrected amount"
                value={editFinalAgreementAmount}
                onChange={(e) => setEditFinalAgreementAmount(e.target.value)}
                min="0"
                step="1000"
              />
            </div>
            <div className="form-group">
              <label>Reason for correction (Optional)</label>
              <textarea
                className="form-textarea"
                rows="3"
                placeholder="Explain why you're correcting the amount..."
                value={editAgreementNote}
                onChange={(e) => setEditAgreementNote(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditFinalAgreementModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditFinalAgreement} disabled={isEditingFinalAgreement || !editFinalAgreementAmount}>
            {isEditingFinalAgreement ? 'Updating...' : 'Update Agreement'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Viewer Modal */}
      <Modal show={showImageViewer} onHide={() => setShowImageViewer(false)} centered size="lg" className="image-viewer-modal">
        <Modal.Header closeButton>
          <Modal.Title>View Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="image-viewer-body">
          {selectedImageForView && <img src={selectedImageForView} alt="Full size" className="full-image" />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageViewer(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={() => window.open(selectedImageForView, '_blank')}>
            <FaDownload /> Download
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withAuth(ArtisanMessages, 'artisan');